import { useState, useEffect, useContext, useCallback } from "react";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { useToastHandler } from "../../../common/ToastHandler";
import AuthContext from "../../../../utils/secure-route/AuthContext";

const useDebounce = (callback, delay) => {
  const [debounceTimer, setDebounceTimer] = useState(null);
  
  return useCallback((...args) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      callback(...args);
    }, delay);
    setDebounceTimer(timer);
  }, [callback, delay, debounceTimer]);
};



const EmployeeEnrollmentController = () => {
  const { Post, Delete , _Get, GetPaginatedData } = useApi();
  const {handleToast} = useToastHandler();
  const { user } = useContext(AuthContext);
  const [employees, setEmployees] = useState([]);
  const [enrolledEmployees, setEnrolledEmployees] = useState([]);
  const [biometricEmployees, setBiometricEmployees] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedBiometricEmployee, setSelectedBiometricEmployee] = useState();
  const [selectedEmployeeObj, setSelectedEmployeeObj] = useState(null);
  const [selectedBiometricEmployeeObj, setSelectedBiometricEmployeeObj] = useState(null);
  const [selectedMachineObj, setSelectedMachineObj] = useState(null);
  const [showModalEmployee, setShowModalEmployee] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);


  const getEnrolledEmployeesData = async (page = 1, search = "") => {
    setIsLoadingData(true);
    try {
      const response = await GetPaginatedData("facebioQueData", {
        page: page,
        search: search
      });
      const sortedData = response.data.sort((a, b) => {
        if (a.request_active === "YES" && b.request_active !== "YES") {
          return -1;
        }
        if (a.request_active !== "YES" && b.request_active === "YES") {
          return 1;
        }
        return 0;
      });


      const pad = (n) => (n < 10 ? "0" + n : n);
      const formattedData = sortedData.map((item) => {
        const date = new Date(item.created_at);
        const day = pad(date.getDate());
        const month = pad(date.getMonth() + 1);
        const year = date.getFullYear();
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());
        const formattedDate = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        return { ...item, created_at: formattedDate };
      });
      setEnrolledEmployees(formattedData);
      setTotalPages(Math.ceil((response.totalCount || response.count || 0) / 10) || 1);
      setTotalCount(response.totalCount || response.count || 0);
    } catch (error) {
      console.error("Error fetching enrolled employees:", error);
      handleToast({
        type: "ERROR",
        message: "Error fetching enrolled employees.",
      });
    } finally {
      setIsLoadingData(false);
    }
  };



  const handleOpenModal = () => {
    setShowModalEmployee(true);
  };


  const handleCloseModal = () => {
    setShowModalEmployee(false);
  };


 useEffect(() => {
    setLoading(true);
   _Get("user")
  .then((response) => {
    const allUsers = response.results || [];
    const storeEmployees = allUsers.filter(
      (u) =>
        u.ms_role_name === "store employee" ||
        u.ms_role_name === "store manager"
    );
    const biometricUsers = allUsers.filter(
      (u) => u.ms_role_name === "biometric_user"
    );
    setEmployees(storeEmployees);
    setBiometricEmployees(biometricUsers);
  })
      .catch((error) => {
        console.error("Error fetching employees:", error);
      }).finally(() => {
        setLoading(false);
      });
  }, []);


  useEffect(() => {
    getEnrolledEmployeesData();
  }, [reloadData]);


  const handleDeleteEnrollment = (id) => {
    Delete("facebioQueData", id)
      .then(() => {
        setEnrolledEmployees((prevEnrolledEmployees) =>
          prevEnrolledEmployees.filter((enrollment) => enrollment.id !== id)
        );
        handleToast({
          type:"SUCCESS" ,
          message:"Enrollment deleted successfully!"
        })    
      })
      .catch((error) => {
        console.error("Error deleting enrollment:", error);
        handleToast({
          type:"ERROR" ,
          message:"Failed to delete enrollment. Please try again."
        }) 
      });
  };



 const handleMachineChange = (opt) => {
    setSelectedMachine(opt?.id || "");
    setSelectedMachineObj(opt);
  };


 const handleEmployeeChange = (emp) => {
  setSelectedEmployee(emp?.id || "");
  setSelectedEmployeeObj(emp);
};


const handleBiometricEmployeeChange = (emp) => {
  setSelectedBiometricEmployee(emp?.id || "");
  setSelectedBiometricEmployeeObj(emp);
};


const handleSubmit = (event) => {
  event.preventDefault();
  const hasPendingEnrollment = enrolledEmployees.some(
    (item) => item.request_active === true && item.is_registered === false
  );
  
  if (hasPendingEnrollment) {
    handleToast({
      type: "ERROR",
      message: "Cannot add new employee. Please complete pending enrollments first.",
    });
    return;
  }

  const employeeName = selectedEmployeeObj ? `${selectedEmployeeObj.first_name} ${selectedEmployeeObj.last_name || ""}`.trim() : "";
  const biometricUserName = selectedBiometricEmployeeObj ? `${selectedBiometricEmployeeObj.first_name} ${selectedBiometricEmployeeObj.last_name || ""}`.trim() : "";
  const payload = {
    employee_id: parseInt(selectedEmployee),
    employee_name: employeeName,
    biometric_user_id: parseInt(selectedBiometricEmployee),
    biometric_user_name: biometricUserName,
    store: user.store

  };
  Post("facebioQueData", payload)
    .then((response) => {
      handleToast({
        type: "SUCCESS",
        message: "Employee enrolled successfully!",
      });
      setReloadData((prev) => !prev);
      resetForm();
    })
    .catch(() => {
      handleToast({
        type: "ERROR",
        message: "Enrollment failed",
      });
    });
};


const resetForm = () => {
  setSelectedEmployee("");
  setSelectedBiometricEmployee("");
  setSelectedEmployeeObj(null);
  setSelectedBiometricEmployeeObj(null);
  setSelectedMachine("");
  setSelectedMachineObj(null);
};

  const debouncedGetEnrolledEmployeesData = useDebounce(getEnrolledEmployeesData, 1000);

  return {
    employees,
    biometricEmployees,
    enrolledEmployees,
    selectedMachine,
    selectedEmployee,
    selectedBiometricEmployee,
    selectedMachineObj,
    selectedEmployeeObj,
    selectedBiometricEmployeeObj,
    handleMachineChange,
    handleEmployeeChange,
    handleBiometricEmployeeChange,
    handleSubmit,
    handleOpenModal,
    handleCloseModal,
    showModalEmployee,
    setShowModalEmployee,
    handleDeleteEnrollment,
    getEnrolledEmployeesData,
    debouncedGetEnrolledEmployeesData,
    isLoadingData,
    totalPages,
    totalCount
  };
};
export default EmployeeEnrollmentController;